import { Injectable } from '@angular/core';
import { LOCALSTORAGEKEYS } from '../../_constants/localStorageKeys';
import { GeoLevel } from '../../_models/geography.model';
import { GeographyService } from '../_geography/geography.service';
import { LocalService } from '../_storage/local.service';

export interface AppRole {
    code: string;
    label: string;
    level: GeoLevel | 'clf';
    readOnly?: boolean;
}

const UPPER_WORDS = ['ib', 'fi', 'mclf', 'clf'];

function formatRoleLabel(code: string): string {
    const base = code.includes(':') ? code.split(':').pop() || code : code;
    if (!base.includes('-')) {
        return base;
    }
    return base
        .split('-')
        .map((word) =>
            UPPER_WORDS.includes(word.toLowerCase())
                ? word.toUpperCase()
                : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(' ');
}

const APP_ROLE_DEFS: AppRole[] = [
    { code: 'national-manager-ib', level: 'national' },
    { code: 'state-manager-ib', level: 'state' },
    { code: 'district-manager-ib', level: 'district' },
    { code: 'block-manager-ib', level: 'block' },
    {
        code: 'Institutional Building:CLF Manager',
        level: 'clf',
        label: 'CLF Manager',
    },
    {
        code: 'Institutional Building:CLF MIS Assistant',
        level: 'clf',
        label: 'CLF Assistant',
    },
].map(
    (def) =>
        ({
            ...def,
            label: (def as AppRole).label || formatRoleLabel(def.code),
        }) as AppRole
);

const KNOWN_ROLE_CODES = new Set(APP_ROLE_DEFS.map((d) => d.code));

@Injectable({
    providedIn: 'root'
})
export class RoleService {
    constructor(
        private localService: LocalService,
        private geographyService: GeographyService
    ) { }

    get availableRoles(): AppRole[] {
        const tokenRoles = this.tokenRoles();
        let roles = APP_ROLE_DEFS.filter((def) => tokenRoles.includes(def.code));
        const clfRole = this.clfRoleFromPermissions();
        if (clfRole) {
            roles = roles.filter((r) => r.code !== 'block-manager-ib');
            if (!roles.some((r) => r.code === clfRole.code)) {
                roles = [clfRole, ...roles];
            }
        }
        // Any other geo-scoped role (not a known manager / CLF role) gets
        // read-only access to the whole application.
        const included = new Set(roles.map((r) => r.code));
        for (const ro of this.readOnlyRolesFromGeography(included)) {
            roles = [...roles, ro];
        }
        return roles;
    }

    private readOnlyRolesFromGeography(included: Set<string>): AppRole[] {
        const result: AppRole[] = [];
        for (const p of this.geographyService.permissions) {
            const code = `${p?.role ?? ''}`;
            if (!code || included.has(code)) {
                continue;
            }
            if (KNOWN_ROLE_CODES.has(code) || this.isClfRoleCode(code)) {
                continue;
            }
            included.add(code);
            result.push({
                code,
                level: p.level,
                label: formatRoleLabel(code),
                readOnly: true,
            });
        }
        return result;
    }

    private isClfRoleCode(code: string): boolean {
        const lower = code.toLowerCase();
        return (
            lower.includes('cadre') ||
            lower.includes('bookkeeper') ||
            lower.includes('mclf') ||
            lower.includes('clf')
        );
    }

    private clfRoleFromPermissions(): AppRole | null {
        const perms = this.userPermissions();
        const match = APP_ROLE_DEFS.find(
            (def) => def.level === 'clf' && perms.includes(def.code)
        );
        return match || null;
    }

    private userPermissions(): string[] {
        const perms = this.localService.get(
            LOCALSTORAGEKEYS.USERPERMISSIONS
        ) as string[] | null;
        return Array.isArray(perms) ? perms : [];
    }

    get selectedRole(): AppRole | null {
        const available = this.availableRoles;
        if (!available.length) {
            return null;
        }
        const stored = this.localService.get(
            LOCALSTORAGEKEYS.SELECTED_ROLE
        ) as string | null;
        return (
            available.find((r) => r.code === stored) ||
            this.defaultRole(available)
        );
    }

    private defaultRole(available: AppRole[]): AppRole {
        const clf = available.find((r) => r.level === 'clf');
        if (clf) {
            return clf;
        }
        const block = available.find((r) => r.code === 'block-manager-ib');
        if (block) {
            return block;
        }
        const order: GeoLevel[] = ['block', 'district', 'state', 'national'];
        for (const level of order) {
            const match = available.find((r) => r.level === level);
            if (match) {
                return match;
            }
        }
        return available[0];
    }

    get selectedLevel(): GeoLevel | 'clf' {
        return this.selectedRole?.level || 'clf';
    }

    get canSwitch(): boolean {
        return this.availableRoles.length > 1;
    }

    get userClf(): { clfCode: string; clfName: string } | null {
        if (this.selectedLevel !== 'clf') {
            return null;
        }
        const stored = this.localService.get(LOCALSTORAGEKEYS.USER_CLF) as {
            clfCode?: string;
            clfName?: string;
        } | null;
        if (!stored?.clfCode) {
            return null;
        }
        return { clfCode: stored.clfCode, clfName: stored.clfName || '' };
    }

    init(): void {
        const selected = this.selectedRole;
        if (!selected) {
            return;
        }
        this.persist(selected);
        this.alignGeography(selected);
    }

    switchRole(role: AppRole): void {
        if (this.selectedRole?.code === role.code) {
            return;
        }
        this.persist(role);
        this.alignGeography(role);
        window.location.href = '/main/inputs/profiles';
    }

    clear(): void {
        this.localService.remove(LOCALSTORAGEKEYS.SELECTED_ROLE);
        this.localService.remove(LOCALSTORAGEKEYS.USERROLES);
    }

    private persist(role: AppRole): void {
        this.localService.set(LOCALSTORAGEKEYS.SELECTED_ROLE, role.code);
        this.localService.set(LOCALSTORAGEKEYS.USERROLES, [role.code]);
    }

    private alignGeography(role: AppRole): void {
        const match = this.geographyService.permissions.find(
            (p) => p.role === role.code
        );
        if (match) {
            this.geographyService.setActive(match);
        }
    }

    private tokenRoles(): string[] {
        const profile = this.localService.get(LOCALSTORAGEKEYS.USERPROFILE) as
            | { resource_access?: Record<string, { roles?: string[] }> }
            | null;
        const access = profile?.resource_access || {};
        const all: string[] = [];
        Object.keys(access).forEach((key) => {
            const list = access[key]?.roles;
            if (Array.isArray(list)) {
                all.push(...list);
            }
        });
        return all;
    }
}
